import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'vot_favourites';

/**
 * Локальные favourites — id блюд, которые гость пометил сердечком.
 * Хранятся в localStorage (не per-uid, потому что anonymous-uid пересоздаётся
 * при чистке cookies; пользователь хочет, чтобы любимые жили дольше).
 *
 * Store — модульный (useSyncExternalStore), а не per-component useState:
 * хук вызывается сразу в MenuItemCard × N и Home, и все инстансы должны
 * видеть toggle мгновенно. Событие `storage` покрывает только другие
 * вкладки — в своей оно не стреляет.
 */
function readStorage(): Set<number> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return new Set<number>();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set<number>();
    return new Set<number>(arr.filter((n: unknown): n is number => typeof n === 'number'));
  } catch {
    return new Set<number>();
  }
}

function writeStorage(ids: Set<number>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // private mode / quota — игнорируем
  }
}

let current: Set<number> = readStorage();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): Set<number> {
  return current;
}

// Кросс-таб синк: другая вкладка поменяла storage — перечитываем.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === KEY) {
      current = readStorage();
      emit();
    }
  });
}

function toggleFav(id: number): void {
  const next = new Set<number>(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  current = next;
  writeStorage(next);
  emit();
}

export function useFavourites() {
  const favs = useSyncExternalStore(subscribe, getSnapshot);
  const toggle = useCallback((id: number) => toggleFav(id), []);
  const has = useCallback((id: number) => favs.has(id), [favs]);
  return { favs, toggle, has };
}
